import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Card, Button, Pill } from "./Primitives";
import api, { formatApiError } from "../../lib/api";
import { Monitor, LogOut, RefreshCw } from "lucide-react";

/**
 * SessionDevicesCard — lists the caller's active sign-in sessions and lets
 * them revoke individual devices. Called from Startup/Investor/Admin
 * Settings pages so every role has device visibility.
 */
export default function SessionDevicesCard() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [showAll, setShowAll] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/auth/sessions");
      setRows(data?.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const revoke = async (session_id, isCurrent) => {
    if (isCurrent) {
      if (!window.confirm("Revoking THIS device will sign you out. Continue?")) return;
    } else {
      if (!window.confirm("Revoke this device? Anyone signed in there will need to log in again.")) return;
    }
    setBusyId(session_id);
    try {
      await api.delete(`/auth/sessions/${session_id}`);
      toast.success("Device signed out.");
      if (isCurrent) {
        // Force a fresh login for the caller.
        try { await api.post("/auth/logout"); } catch { /* ignore */ }
        window.location.assign("/login");
        return;
      }
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusyId(null); }
  };

  const label = (ua) => {
    if (!ua) return "Unknown device";
    const parts = [];
    if (/Mobile|iPhone|Android/i.test(ua)) parts.push("Mobile");
    else if (/iPad|Tablet/i.test(ua)) parts.push("Tablet");
    else parts.push("Desktop");
    if (/Chrome/i.test(ua) && !/Edg/i.test(ua)) parts.push("Chrome");
    else if (/Firefox/i.test(ua)) parts.push("Firefox");
    else if (/Safari/i.test(ua)) parts.push("Safari");
    else if (/Edg/i.test(ua)) parts.push("Edge");
    if (/Mac OS X|Macintosh/i.test(ua)) parts.push("macOS");
    else if (/Windows/i.test(ua)) parts.push("Windows");
    else if (/Linux/i.test(ua)) parts.push("Linux");
    else if (/Android/i.test(ua)) parts.push("Android");
    else if (/iPhone|iPad|iOS/i.test(ua)) parts.push("iOS");
    return parts.join(" · ");
  };

  return (
    <Card>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="overline">Signed-in Devices</div>
          <div className="text-xs text-ink-muted mt-1">
            Anywhere you&apos;re currently signed in. Revoke a device to sign it out instantly.
          </div>
        </div>
        <Button variant="ghost" onClick={load} data-testid="sessions-refresh">
          <RefreshCw size={13} className="inline mr-1" /> Refresh
        </Button>
      </div>
      {loading && <div className="text-sm text-ink-muted">Loading sessions…</div>}
      {!loading && rows.length === 0 && (
        <div className="text-sm text-ink-muted">No active sessions.</div>
      )}
      <div className="space-y-2" data-testid="sessions-list">
        {(showAll ? rows : rows.slice(0, 6)).map((r) => (
          <div key={r.session_id}
               className="flex items-center justify-between gap-3 border border-navy/10 rounded-lg px-3 py-2 bg-white"
               data-testid={`session-row-${r.session_id}`}>
            <div className="flex items-start gap-3 min-w-0">
              <Monitor size={16} className="text-ink-muted mt-0.5 shrink-0" />
              <div className="min-w-0">
                <div className="text-sm text-ink font-heading flex items-center gap-2 flex-wrap">
                  {label(r.user_agent)}
                  {r.current && <Pill tone="success">This device</Pill>}
                  {r.kind === "google" && <Pill tone="gold">Google</Pill>}
                  {r.persist && !r.current && <Pill tone="muted">Remembered</Pill>}
                </div>
                <div className="text-[11px] font-mono text-ink-muted mt-0.5 truncate">
                  {r.ip || "IP unknown"} · Signed in {(r.created_at || "").slice(0, 16)}
                  {r.last_seen_at && ` · Last active ${(r.last_seen_at || "").slice(0, 16)}`}
                </div>
              </div>
            </div>
            <Button
              variant="danger"
              disabled={busyId === r.session_id}
              onClick={() => revoke(r.session_id, r.current)}
              data-testid={`session-revoke-${r.session_id}`}
            >
              <LogOut size={12} className="inline mr-1" />
              {busyId === r.session_id ? "…" : (r.current ? "Sign out here" : "Revoke")}
            </Button>
          </div>
        ))}
      </div>
      {!loading && rows.length > 6 && (
        <button
          className="mt-3 text-xs font-mono text-emerald-800 hover:underline"
          onClick={() => setShowAll((v) => !v)}
          data-testid="sessions-toggle-showall"
        >
          {showAll ? "Show fewer" : `Show all ${rows.length} devices`}
        </button>
      )}
    </Card>
  );
}
